<script lang="ts" setup>
import {
  MediaImageFragment,
  TaxonomyTermToccoEventTeachingMethodFieldsFragment,
  ToccoBaseModuleFieldsFragment,
  ToccoUser,
} from '~/api/graphql/generated'
import { AuthorProps } from '../sidebar/components/AuthorSidebar.vue'
import { formatToccoPrice } from '~/lib/utils/toccoUtils'

const { t } = useI18n()

const props = defineProps<{
  entity: ToccoBaseModuleFieldsFragment
}>()

const logos = computed(
  () =>
    (props.entity.fieldImages
      ?.map((media) => media?.entity as MediaImageFragment)
      .filter(Boolean) as MediaImageFragment[]) ?? [],
)

const author = computed(() => {
  const user = props.entity.fieldAdministrator?.entity as ToccoUser
  const firstName = user?.fieldFirstName ? `${user.fieldFirstName} ` : ''
  const lastName = user?.fieldLastName ? `${user.fieldLastName} ` : ''
  const fullName = `${firstName}${lastName}`.trim()

  return {
    image: (user?.fieldProfilePicture?.entity as MediaImageFragment) || {
      fieldMediaImage: { url: '/tocco-user-placeholder.png', height: 135, width: 135 },
    },
    name: fullName,
    alt: fullName,
    job: user?.fieldJob,
    email: user?.fieldEmail,
    phone: user?.fieldPhoneNumber,
  }
})

const formattedCost = computed(() => {
  const price = formatToccoPrice(props.entity.fieldPrice)
  if (price) return price
  return props.entity.fieldCosts?.value?.split('\n').join('<br/>') ?? ''
})

const sidebarLabelValueItems = computed(() => {
  const items: Array<{ label: string; value: string }> = []
  if (props.entity.fieldToccoAbbreviation) {
    items.push({
      label: t('tocco.detail.sidebarTableLabels.number'),
      value: props.entity.fieldToccoAbbreviation,
    })
  }
  if (props.entity.fieldTimes?.value) {
    items.push({
      label: t('tocco.detail.sidebarTableLabels.time'),
      value: props.entity.fieldTimes.value.split('\n').join('<br/>'),
    })
  }
  if (formattedCost.value) {
    items.push({
      label: t('tocco.detail.sidebarTableLabels.cost'),
      value: formattedCost.value,
    })
  }
  const teachingMethod = props.entity.fieldTeachingMethod?.entity as TaxonomyTermToccoEventTeachingMethodFieldsFragment
  if (teachingMethod?.name) {
    items.push({
      label: t('tocco.detail.sidebarTableLabels.teachingMethod'),
      value: teachingMethod.name,
    })
  }
  return items
})
</script>

<template>
  <SectionSidebar>
    <SidebarSection v-if="logos.length" class="tocco-sidebar-logos">
      <Image v-for="(logo, i) in logos" :key="i" :entity="logo" type="sidebar-logo" />
    </SidebarSection>

    <AuthorSidebar :title="t('contact')" :author="author as AuthorProps" />

    <TextSidebar
      :title="t('tocco.detail.sidebarLocation')"
      text="<p>Careum Weiterbildung, Mühlemattstrasse 42, 5000 Aarau</p>"
    />

    <LabelValueSidebar :items="sidebarLabelValueItems" />
  </SectionSidebar>
</template>

<style lang="scss" scoped>
.tocco-sidebar-logos {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: center;

  :deep(img) {
    width: 100%;
    max-width: 105px;
    height: auto;

    @include breakpoint(tl) {
      max-width: 140px;
    }
  }
}
</style>
